import React, { useEffect, useState } from 'react';
import {
  VStack,
  Heading,
  Text,
  Box,
  Button,
  useColorModeValue,
  Spinner,
  Icon,
  SimpleGrid,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { StarIcon } from '@chakra-ui/icons';
import { FaBrain, FaRocket, FaGraduationCap } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboardingStore } from '../../../stores/onboardingStore';
import { useAuth } from '../../../hooks/useAuth';
import { userService } from '../../../api/userService';

const CompletionStep: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { fetchUserProfile } = useAuth();
  const { userData, resetOnboarding } = useOnboardingStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const isTestMode = location.pathname.startsWith('/onboarding-test');
  
  const headingGradient = useColorModeValue(
    'linear(to-r, green.600, blue.600)',
    'linear(to-r, green.400, blue.400)'
  );
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('green.50', 'gray.700');
  const iconColor = useColorModeValue('green.500', 'green.400');

  useEffect(() => {
    // Submit the profile data when component mounts (skip in test mode)
    if (!isTestMode) {
      submitProfile();
    } else {
      // In test mode, just mark as complete
      setIsComplete(true);
    }
  }, [isTestMode]);

  const submitProfile = async () => {
    if (isSubmitting || isComplete) return;
    
    setIsSubmitting(true);
    try {
      // Transform data to match API format
      // Shape payload to match backend UserUpdate schema:
      // - top-level: first_name, last_name, birthday
      // - nested profile: maps onboarding fields to UserProfile columns
      const profileData = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        birthday: userData.birthday ?
          (userData.birthday instanceof Date ?
            userData.birthday.toISOString().split('T')[0] :
            new Date(userData.birthday).toISOString().split('T')[0]
          ) : undefined,
        profile: {
          school_organization: userData.school || undefined,
          current_grade_level: userData.grade || undefined,
          // Backend schema expects Json[Any] → send as JSON string
          profile_settings: JSON.stringify({
            pronouns: userData.pronouns || undefined,
            learning_avatar: userData.learningAvatar || undefined,
            favorite_courses: userData.favoriteCourses || undefined,
            challenging_courses: userData.challengingCourses || undefined,
          }),
        },
      } as const;

      // Update profile
      await userService.completeUserProfile(profileData);
      
      // Refresh user data
      await fetchUserProfile();
      
      setIsComplete(true);
      toast({
        title: 'Profile completed!',
        description: 'Your learning journey begins now.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Clear onboarding data from localStorage
      resetOnboarding();
      
    } catch (error) {
      console.error('Failed to complete profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const nextSteps = [
    {
      icon: FaBrain,
      title: 'Start Learning',
      description: 'Chat with your AI tutor',
      action: () => navigate('/chat'),
    },
    {
      icon: FaRocket,
      title: 'Upload Materials',
      description: 'Add your study materials',
      action: () => navigate('/documents'),
    },
  ];

  if (isSubmitting && !isComplete) {
    return (
      <VStack 
        spacing={6} 
        align="center" 
        justify="center" 
        minH="60vh"
        w="full"
      >
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text fontSize="lg" color={textColor}>
          Setting up your personalized learning experience...
        </Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={8} align="center" maxW="600px" mx="auto">
        {/* Success Message */}
        <VStack spacing={4} align="center" textAlign="center">
          {React.createElement(FaGraduationCap as unknown as React.ElementType, { size: 80, color: iconColor as any })}
          <Heading
            size="xl"
            bgGradient={headingGradient}
            bgClip="text"
            fontWeight="bold"
          >
            Welcome Aboard, {userData.firstName}!
          </Heading>
          <Text fontSize="lg" color={textColor}>
            Your profile is complete and your personalized learning journey is ready.
          </Text>
        </VStack>

        {/* Summary */}
        <Box bg={cardBg} p={4} borderRadius="lg" w="full">
          <Text fontWeight="semibold" mb={2}>Your Learning Profile:</Text>
          <SimpleGrid columns={2} spacing={2} fontSize="sm">
            <Text>Learning Style:</Text>
            <Text fontWeight="bold">{userData.learningAvatar}</Text>
            <Text>School:</Text>
            <Text fontWeight="bold">{userData.school}</Text>
            <Text>Grade:</Text>
            <Text fontWeight="bold">{userData.grade}</Text>
            <Text>Focus Areas:</Text>
            <Text fontWeight="bold">{userData.favoriteCourses.length + userData.challengingCourses.length} courses</Text>
          </SimpleGrid>
        </Box>

        {/* Next Steps */}
        <VStack spacing={3} align="stretch" w="full">
          <Text fontWeight="semibold" fontSize="lg" textAlign="center">What would you like to do first?</Text>
          <VStack spacing={3} w="full">
            {nextSteps.map((step) => (
              <Button
                key={step.title}
                onClick={step.action}
                variant="outline"
                size="lg"
                p={6}
                height="auto"
                justifyContent="start"
                w="full"
              >
                <Flex align="center" gap={3}>
                  {React.createElement(step.icon as unknown as React.ElementType, { size: 24, color: iconColor as any })}
                  <Box textAlign="left">
                    <Text fontWeight="semibold">{step.title}</Text>
                    <Text fontSize="sm" color={textColor} fontWeight="normal">
                      {step.description}
                    </Text>
                  </Box>
                </Flex>
              </Button>
            ))}
          </VStack>
        </VStack>
      </VStack>
  );
};

export default CompletionStep;
